import { Cookies as GameCookies } from '@gamesite/game-core'
import { configurationCookiesConfig, consoleLogAPI } from 'src/common/functions'
import {
  UZBEK_CYRILLIC_ENABLED,
  getStoredLocale,
  setStoredLocale,
  setStoredLocaleFromProfile,
} from 'src/common/utils/locale'
import { Cookies as BrowserCookies } from 'src/core/utils/cookies'
import getCurrentUser from 'src/apollo/queries/getCurrentUser.gql'

// Внешний URL-параметр для redirect после регистрации.
// Общий helper остаётся один (`redirectAfterAuth`), но сам параметр
// влияет только на first-auth / registration сценарии.
const REDIRECT_PARAM = 'redirect_after_reg',
  COOKIE_EXPIRES = '1d'

// Адаптер для общего boot/config механизма сохранения GET-параметров.
export const REDIRECT_AFTER_AUTH = {
  query: REDIRECT_PARAM,
  storage: {
    getItem: getStoredRedirect,
    setItem: (_, value) => setStoredRedirect(value),
    removeItem: clearStoredRedirect,
  },
}

export function isLandingRoute(route) {
  return route?.matched?.some((record) => record.path?.endsWith('/land')) ?? false
}

// После social auth дотягиваем currentUser и сохраняем только те сайд-эффекты,
// которые исторически ожидались для лендингов.
async function applySocialRedirectState({ apolloClient, route, socialToken, onError, preserveLocale = false } = {}) {
  if (!socialToken || !apolloClient) {
    return
  }

  const isLandingRegistration = isLandingRoute(route)

  try {
    const result = await apolloClient.query({
      query: getCurrentUser,
      fetchPolicy: 'network-only',
    })
    const user = result?.data?.currentUser ?? null

    if (user?.language) {
      setStoredLocaleFromProfile(user.language, { preserveCurrent: preserveLocale })
    }

    if (isLandingRegistration && user) {
      GameCookies.set('user', JSON.stringify(user), configurationCookiesConfig(socialToken, { path: '/' }))
    }
  } catch (e) {
    consoleLogAPI(e)

    if (isLandingRegistration) {
      onError?.(e)
    }
  }
}

// Принимаем только внутренние пути сайта.
// Заодно нормализуем ссылки без начального слэша и убираем хвостовые слэши.
function normalizeRedirect(value) {
  const rawValue = Array.isArray(value) ? value[0] : value

  if (typeof rawValue !== 'string') {
    return null
  }

  let trimmedValue = rawValue.trim()

  try {
    trimmedValue = decodeURIComponent(trimmedValue)
  } catch {
    trimmedValue = rawValue.trim()
  }

  if (
    !trimmedValue ||
    trimmedValue.startsWith('//') ||
    /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmedValue) ||
    /[\r\n\\]/.test(trimmedValue)
  ) {
    return null
  }

  const match = trimmedValue.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/)

  if (!match) {
    return null
  }

  let [, pathname = '', search = '', hash = ''] = match

  // Служебный query-параметр не должен попадать в финальный redirect path.
  if (search) {
    const searchParams = new URLSearchParams(search)

    searchParams.delete(REDIRECT_PARAM)

    const normalizedSearch = searchParams.toString()
    search = normalizedSearch ? `?${normalizedSearch}` : ''
  }

  pathname = pathname.startsWith('/') ? pathname : `/${pathname}`

  if (pathname !== '/') {
    pathname = pathname.replace(/\/+$/, '')
  }

  return `${pathname}${search}${hash}`
}

function decodeQueryValue(value) {
  const stringValue = String(value)

  try {
    return decodeURIComponent(stringValue)
  } catch {
    return stringValue
  }
}

function appendRouteQueryToRedirect(redirectPath, routeQuery) {
  const normalizedRedirect = normalizeRedirect(redirectPath)

  if (!normalizedRedirect || !routeQuery || typeof routeQuery !== 'object') {
    return normalizedRedirect
  }

  const match = normalizedRedirect.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/)

  if (!match) {
    return normalizedRedirect
  }

  const [, pathname = '', search = '', hash = ''] = match
  const searchParams = new URLSearchParams(search)

  for (const [key, rawValue] of Object.entries(routeQuery)) {
    if (key === REDIRECT_PARAM || rawValue === null || rawValue === undefined || searchParams.has(key)) {
      continue
    }

    const values = Array.isArray(rawValue) ? rawValue : [rawValue]

    for (const value of values) {
      if (value !== null && value !== undefined) {
        searchParams.append(key, decodeQueryValue(value))
      }
    }
  }

  const normalizedSearch = searchParams.toString()

  return `${pathname}${normalizedSearch ? `?${normalizedSearch}` : ''}${hash}`
}

// redirect_after_reg живёт и в sessionStorage, и в cookie:
// sessionStorage нужен для текущей сессии, cookie помогает пережить промежуточные переходы.
function clearStoredRedirect() {
  sessionStorage.removeItem(REDIRECT_PARAM)
  BrowserCookies.remove(REDIRECT_PARAM)
}

function getStoredRedirect() {
  return (
    normalizeRedirect(sessionStorage.getItem(REDIRECT_PARAM)) ||
    normalizeRedirect(BrowserCookies.get(REDIRECT_PARAM)) ||
    null
  )
}

function setStoredRedirect(value) {
  const redirect = normalizeRedirect(value)

  if (!redirect) {
    clearStoredRedirect()
    return null
  }

  sessionStorage.setItem(REDIRECT_PARAM, redirect)
  BrowserCookies.set(REDIRECT_PARAM, redirect, { expires: COOKIE_EXPIRES })

  return redirect
}

// Если redirect_after_reg есть в текущем роуте, обновляем storage.
// Если параметр потом исчезает, сохранённое значение остаётся до first-auth redirect.
function syncStoredRedirectFromRoute(route, { includeRouteQuery = false } = {}) {
  const routeQuery = route?.query

  if (!routeQuery || typeof routeQuery !== 'object') {
    return
  }

  if (Object.prototype.hasOwnProperty.call(routeQuery, REDIRECT_PARAM)) {
    setStoredRedirect(
      includeRouteQuery
        ? appendRouteQueryToRedirect(routeQuery[REDIRECT_PARAM], routeQuery)
        : routeQuery[REDIRECT_PARAM],
    )
  }
}

// Собираем итоговый query для финального redirect после auth.
// Приоритет path такой:
// 1. redirect_after_reg, но только для first auth / registration
// 2. landingRoute
// 3. linkAfterAuth
// 4. текущий route.fullPath
export function buildAuthRedirectContext({
  route = null,
  router = null,
  landingRoute = null,
  isFirstAuth = false,
} = {}) {
  syncStoredRedirectFromRoute(route, { includeRouteQuery: isFirstAuth })

  const linkAfterAuth = sessionStorage.getItem('linkAfterAuth')
  const storedRedirect = isFirstAuth ? getStoredRedirect() : null
  const resolvedLandingRoute =
    isFirstAuth && landingRoute && router && isLandingRoute(route) ? router.resolve(landingRoute).fullPath : null
  const routeFallback = route?.fullPath && route.fullPath !== '/' ? route.fullPath : null
  let redirectPath =
    [storedRedirect, resolvedLandingRoute, linkAfterAuth, routeFallback].map(normalizeRedirect).find(Boolean) ?? null
  const query = {}

  if (isFirstAuth) {
    redirectPath = appendRouteQueryToRedirect(redirectPath, { auth: 'first' })
  }

  if (redirectPath) {
    query.path = encodeURIComponent(redirectPath)
  }

  if (isFirstAuth) {
    clearStoredRedirect()
  }

  return query
}

export function addMirrorLocaleToRedirectQuery(query, locale = getStoredLocale()) {
  if (!UZBEK_CYRILLIC_ENABLED || !locale) {
    return query
  }

  return {
    ...query,
    mirror_locale: locale,
  }
}

async function preserveFirstAuthLocale(isFirstAuth) {
  if (!UZBEK_CYRILLIC_ENABLED || !isFirstAuth) {
    return
  }

  try {
    const { i18nObject } = await import('boot/i18n')
    const activeLocale = i18nObject?.global?.locale?.value

    if (activeLocale) {
      setStoredLocale(activeLocale)
    }
  } catch {
    // Early auth flows are handled by core locale resolution before this helper is available.
  }
}

// Единая точка входа для post-auth redirect:
// сначала выполняем social-specific сайд-эффекты,
// потом строим query и отдаём финальное решение checkMirrorAndRedirect.
export async function redirectAfterAuth(options = {}) {
  const { apolloClient, apolloClientGo, socialToken, onSocialCurrentUserError, route, isFirstAuth = false } = options

  await preserveFirstAuthLocale(isFirstAuth)

  await applySocialRedirectState({
    apolloClient,
    route,
    socialToken,
    onError: onSocialCurrentUserError,
    preserveLocale: isFirstAuth,
  })

  const query = addMirrorLocaleToRedirectQuery(buildAuthRedirectContext(options))
  const { checkMirrorAndRedirect } = await import('src/common/functions')

  return checkMirrorAndRedirect({
    apolloClient,
    apolloClientGo,
    query,
  })
}
